/**
 * lifeEventProbs.ts — US demographic life event probability engine.
 *
 * All numeric constants are sourced from src/engine/defaults.ts.
 * HC-DEFAULTS-IMMUTABLE: no bare numeric literals in this file.
 */

import type { SimulationEvent, SimulationEventType, SimulationEventParams, UserProfile } from './types'
import { newEventId } from './events'
import {
  LIFE_EVENT_GENERATION_GAP_YEARS,
  LIFE_EVENT_JOB_LOSS_CYCLE_YEARS,
  LIFE_EVENT_MAX_JOB_LOSSES,
  LIFE_EVENT_MARKET_CRASH_CYCLE_YEARS,
  LIFE_EVENT_MAX_MARKET_CRASHES,
  LIFE_EVENT_MEDICAL_SPIKE_AGE,
  LIFE_EVENT_DISABILITY_YEAR_PCT,
  LIFE_EVENT_PARENT_CARE_TRIGGER_AGE,
  LIFE_EVENT_PARENT_CARE_DURATION,
  LIFE_EVENT_FATHER_LIFE_EXPECTANCY,
  LIFE_EVENT_MOTHER_LIFE_EXPECTANCY,
  LIFE_EVENT_FIRST_HOME_AGE,
  LIFE_EVENT_HOME_PRICE_MULTIPLE,
  LIFE_EVENT_MIN_HOME_PRICE,
  LIFE_EVENT_BONUS_INCOME_THRESHOLD,
  LIFE_EVENT_BONUS_PCT,
  LIFE_EVENT_BONUS_CYCLE_YEARS,
  LIFE_EVENT_VEHICLE_YEAR_PCT,
  LIFE_EVENT_VEHICLE_COST,
  LIFE_EVENT_VEHICLE2_COST,
  LIFE_EVENT_VEHICLE_CYCLE_YEARS,
  LIFE_EVENT_GRANDCHILD_AGE,
  LIFE_EVENT_GRANDCHILD_COST,
  LIFE_EVENT_GRANDCHILD_SPREAD,
  LIFE_EVENT_COLLEGE_OFFSET_BASE,
  LIFE_EVENT_COLLEGE_OFFSET_STAGGER,
  LIFE_EVENT_INHERITANCE_HIGH_INCOME,
  LIFE_EVENT_INHERITANCE_MID_INCOME,
  LIFE_EVENT_INHERITANCE_FATHER_HIGH,
  LIFE_EVENT_INHERITANCE_FATHER_MID,
  LIFE_EVENT_INHERITANCE_FATHER_LOW,
  LIFE_EVENT_INHERITANCE_MOTHER_HIGH,
  LIFE_EVENT_INHERITANCE_MOTHER_MID,
  LIFE_EVENT_INHERITANCE_MOTHER_LOW,
  LIFE_EVENT_PARENT_CARE_EARLY_COST,
  LIFE_EVENT_PARENT_CARE_LATE_COST,
  LIFE_EVENT_MEDICAL_COST_INSURED,
  LIFE_EVENT_MEDICAL_DEDUCTIBLE_MAX,
  LIFE_EVENT_MEDICAL_LATE_COST,
  LIFE_EVENT_MEDICAL_LATE_DEDUCTIBLE,
  LIFE_EVENT_MEDICAL_LATE_SPREAD,
  LIFE_EVENT_CHILDCARE_ANNUAL,
  LIFE_EVENT_COLLEGE_ANNUAL,
  DEFAULT_MARKET_LOSS_PCT,
  DEFAULT_MARKET_RECOVERY_YEARS,
  DEFAULT_MARKET_RETURN_PENALTY,
  HALF,
  LCG_MULTIPLIER,
  LCG_INCREMENT,
  LCG_MODULUS,
  LIFE_EVENT_JL_BREAKPOINT_1,
  LIFE_EVENT_JL_BREAKPOINT_2,
  LIFE_EVENT_JL_BREAKPOINT_3,
  LIFE_EVENT_JL_PROB_1,
  LIFE_EVENT_JL_PROB_2,
  LIFE_EVENT_JL_PROB_3,
  LIFE_EVENT_JL_INCOME_FRACTION,
  LIFE_EVENT_JL_DURATION_YEARS,
  LIFE_EVENT_MEDICAL_YOUNG_PCT,
  LIFE_EVENT_MEDICAL_OLD_PCT,
  LIFE_EVENT_MEDICAL_YOUNG_AGE_CUTOFF,
  LIFE_EVENT_DISABILITY_INCOME_FRACTION,
  LIFE_EVENT_DISABILITY_DURATION,
  LIFE_EVENT_END_GUARD_5,
  LIFE_EVENT_GRANDCHILD_END_GUARD,
  LIFE_EVENT_MEDICAL_LATE_END_GUARD,
  LIFE_EVENT_MIN_PROJECTION_MEDICAL,
  LIFE_EVENT_MIN_PROJECTION_BONUS,
  LIFE_EVENT_MIN_PROJECTION_DISABILITY,
  LIFE_EVENT_COLLEGE_END_GUARD,
  LIFE_EVENT_HOME_PRICE_ROUNDING,
  LIFE_EVENT_BONUS_ROUNDING,
} from './defaults'

export interface EventRecommendation {
  type: SimulationEventType
  label: string
  startYearOffset: number
  params: SimulationEventParams
  probability: number    // 0–1 chance this specific event should be included
  rationale: string      // injected into LLM context
}

// CURRENT_YEAR derived at runtime — no hardcoded year
const CURRENT_YEAR = new Date().getFullYear()

// Job loss breakpoints and probabilities — use imported constants
const JL_BREAKPOINTS: [number, number, number] = [
  LIFE_EVENT_JL_BREAKPOINT_1,
  LIFE_EVENT_JL_BREAKPOINT_2,
  LIFE_EVENT_JL_BREAKPOINT_3,
]
const JL_PROBS: [number, number, number] = [
  LIFE_EVENT_JL_PROB_1,
  LIFE_EVENT_JL_PROB_2,
  LIFE_EVENT_JL_PROB_3,
]

// Medical age cutoff for young vs late percentages
const MEDICAL_YOUNG_PCT = LIFE_EVENT_MEDICAL_YOUNG_PCT
const MEDICAL_OLD_PCT = LIFE_EVENT_MEDICAL_OLD_PCT
const MEDICAL_YOUNG_AGE_CUTOFF = LIFE_EVENT_MEDICAL_YOUNG_AGE_CUTOFF

// Home purchase down payment for renters (10% — FHA/conventional lower bound)
const RENTER_HOME_DOWN_PCT = 0.10
const RENTER_HOME_MORTGAGE_RATE = 0.065
const RENTER_HOME_TERM_YEARS = 30
// Home price rounding increment
const HOME_PRICE_ROUNDING = LIFE_EVENT_HOME_PRICE_ROUNDING

// Bonus rounding increment ($k)
const BONUS_ROUNDING = LIFE_EVENT_BONUS_ROUNDING

// Minimum inheritance projection years
const INHERIT_FATHER_MIN_YEARS = HALF
const INHERIT_MOTHER_MIN_YEARS = LIFE_EVENT_END_GUARD_5

// Parent elder-care age threshold for higher-cost care
const PARENT_AGE_HIGH_CARE_THRESHOLD = 70

// Maximum current age to still face parental inheritance
const MAX_AGE_FOR_INHERITANCE = 75

// Maximum current age to still face parent elder-care
const MAX_AGE_FOR_PARENT_CARE = 72

// Maximum current age for disability risk (pre-retirement)
const MAX_AGE_FOR_DISABILITY = 62
const MIN_DISABILITY_PROJECTION_YEARS = LIFE_EVENT_MIN_PROJECTION_DISABILITY

// Minimum projection years for medical events
const MIN_MEDICAL_PROJECTION_YEARS = LIFE_EVENT_MIN_PROJECTION_MEDICAL
// Late-life medical spread years
const MEDICAL_SPREAD_YEARS_LATE = LIFE_EVENT_MEDICAL_LATE_SPREAD

// Vehicle spacing guard: at least N years apart
const VEHICLE_MIN_GAP = LIFE_EVENT_END_GUARD_5

/** Build a list of age-appropriate life event recommendations for a user profile. */
export function buildLifeEventRecommendations(
  profile: UserProfile,
  projectionYears: number,
): EventRecommendation[] {
  const currentAge = profile.birthYear > 0 ? CURRENT_YEAR - profile.birthYear : 40
  const income = profile.annualW2Income + profile.annualEquityComp + profile.annualSelfEmploymentIncome
  const recs: EventRecommendation[] = []

  const estimatedParentAge = currentAge + LIFE_EVENT_GENERATION_GAP_YEARS

  // ── Job Loss ─────────────────────────────────────────────────────────────
  const jobLossCount = Math.min(LIFE_EVENT_MAX_JOB_LOSSES, Math.floor(projectionYears / LIFE_EVENT_JOB_LOSS_CYCLE_YEARS))
  for (let i = 0; i < jobLossCount; i++) {
    recs.push({
      type: 'job-loss',
      label: i === 0 ? 'Layoff / Job Loss' : `Job Disruption #${i + 1}`,
      startYearOffset: clamp(Math.round(projectionYears * JL_BREAKPOINTS[i]), 1, projectionYears - HALF),
      params: { incomeFraction: LIFE_EVENT_JL_INCOME_FRACTION, durationYears: LIFE_EVENT_JL_DURATION_YEARS },
      probability: JL_PROBS[i],
      rationale: `BLS JOLTS: ~1.7%/month layoff rate. ~50% of US workers face at least one layoff in 20 years.`,
    })
  }

  // ── Market Crashes ───────────────────────────────────────────────────────
  const crashCount = Math.min(LIFE_EVENT_MAX_MARKET_CRASHES, Math.floor(projectionYears / LIFE_EVENT_MARKET_CRASH_CYCLE_YEARS))
  for (let i = 0; i < crashCount; i++) {
    const yearOffset = clamp(Math.round((i + 1) * projectionYears / (crashCount + 1)), HALF, projectionYears - HALF)
    const lossPct = DEFAULT_MARKET_LOSS_PCT + (i % HALF) * 0.15
    const recYears = DEFAULT_MARKET_RECOVERY_YEARS + (i % HALF) * DEFAULT_MARKET_RECOVERY_YEARS
    recs.push({
      type: 'market-shock',
      label: `Market Downturn — Year ${yearOffset}`,
      startYearOffset: yearOffset,
      params: { portfolioLossPct: lossPct, recoveryYears: recYears, recoveryReturnPenalty: DEFAULT_MARKET_RETURN_PENALTY },
      probability: 0.90,
      rationale: `S&P 500: bear markets every ~7-10 years. Avg decline 35%. ${crashCount} expected over ${projectionYears}-year horizon.`,
    })
  }

  // ── Medical Emergency ────────────────────────────────────────────────────
  if (projectionYears > MIN_MEDICAL_PROJECTION_YEARS) {
    const agePct = currentAge < MEDICAL_YOUNG_AGE_CUTOFF ? MEDICAL_YOUNG_PCT : MEDICAL_OLD_PCT
    const medYear1 = clamp(Math.round(projectionYears * agePct), 1, projectionYears - HALF)
    recs.push({
      type: 'medical-emergency',
      label: 'Major Medical Event (Insured)',
      startYearOffset: medYear1,
      params: { cost: LIFE_EVENT_MEDICAL_COST_INSURED, insured: true, deductible: LIFE_EVENT_MEDICAL_DEDUCTIBLE_MAX, spreadYears: 1 },
      probability: 0.80,
      rationale: `KFF 2024: OOP max ~$9,450. Major medical events have ~85% lifetime probability.`,
    })
  }

  const lateLifeAge = LIFE_EVENT_MEDICAL_SPIKE_AGE - currentAge
  if (lateLifeAge > 0 && lateLifeAge < projectionYears - LIFE_EVENT_MEDICAL_LATE_END_GUARD) {
    recs.push({
      type: 'medical-emergency',
      label: 'Late-Life Medical Costs',
      startYearOffset: clamp(lateLifeAge, 1, projectionYears - 3),
      params: { cost: LIFE_EVENT_MEDICAL_LATE_COST, insured: true, deductible: LIFE_EVENT_MEDICAL_LATE_DEDUCTIBLE, spreadYears: MEDICAL_SPREAD_YEARS_LATE },
      probability: 0.70,
      rationale: `CDC: Adults 65+ spend 3× more on healthcare. Late-life OOP can reach $10-15k/year.`,
    })
  }

  // ── Disability ───────────────────────────────────────────────────────────
  if (currentAge < MAX_AGE_FOR_DISABILITY && projectionYears > MIN_DISABILITY_PROJECTION_YEARS) {
    const disabilityYear = clamp(Math.round(projectionYears * LIFE_EVENT_DISABILITY_YEAR_PCT), 3, projectionYears - LIFE_EVENT_MEDICAL_LATE_END_GUARD)
    recs.push({
      type: 'disability',
      label: 'Disability / Serious Illness',
      startYearOffset: disabilityYear,
      params: { incomeFraction: LIFE_EVENT_DISABILITY_INCOME_FRACTION, durationYears: LIFE_EVENT_DISABILITY_DURATION },
      probability: 0.25,
      rationale: `SSA: 1 in 4 working adults become disabled before retirement. SSDI pays ~50% prior wages.`,
    })
  }

  // ── Childcare ────────────────────────────────────────────────────────────
  for (let i = 0; i < profile.childrenCount; i++) {
    recs.push({
      type: 'childcare',
      label: `Childcare — Child ${i + 1}`,
      startYearOffset: i,
      params: { annualCost: LIFE_EVENT_CHILDCARE_ANNUAL, endYearOffset: i + 5 },
      probability: 1.0,
      rationale: `Care.com 2024: US avg center-based childcare $21,000/year (ages 0–5).`,
    })
  }

  // ── College Tuition ──────────────────────────────────────────────────────
  for (let i = 0; i < profile.childrenCount; i++) {
    const collegeOffset = LIFE_EVENT_COLLEGE_OFFSET_BASE + i * LIFE_EVENT_COLLEGE_OFFSET_STAGGER
      if (collegeOffset < projectionYears - LIFE_EVENT_COLLEGE_END_GUARD) {
        recs.push({
          type: 'college-tuition',
          label: `College Tuition — Child ${i + 1}`,
          startYearOffset: collegeOffset,
          params: { annualCost: LIFE_EVENT_COLLEGE_ANNUAL, endYearOffset: collegeOffset + LIFE_EVENT_COLLEGE_END_GUARD },
        probability: 0.70,
        rationale: `College Board 2024-25: avg public 4-yr in-state ~$29k/yr. 4-year duration.`,
      })
    }
  }

  // ── Parent / Elder Care ──────────────────────────────────────────────────
  if (currentAge < MAX_AGE_FOR_PARENT_CARE) {
    const yearsUntilCare = Math.max(1, LIFE_EVENT_PARENT_CARE_TRIGGER_AGE - estimatedParentAge)
    const careEndOffset = yearsUntilCare + LIFE_EVENT_PARENT_CARE_DURATION
    if (yearsUntilCare < projectionYears - HALF) {
      recs.push({
        type: 'long-term-care',
        label: 'Parent Elder Care',
        startYearOffset: clamp(yearsUntilCare, 1, projectionYears - 3),
        params: {
          annualCost: estimatedParentAge >= PARENT_AGE_HIGH_CARE_THRESHOLD
            ? LIFE_EVENT_PARENT_CARE_LATE_COST
            : LIFE_EVENT_PARENT_CARE_EARLY_COST,
          endYearOffset: clamp(careEndOffset, HALF, projectionYears - 1),
        },
        probability: 0.65,
        rationale: `AARP 2023: 53M Americans (21%) provide eldercare. Peak need ~${Math.max(1, Math.round(yearsUntilCare))} years away.`,
      })
    }
  }

  // ── Inheritance / Windfall ───────────────────────────────────────────────
  if (currentAge < MAX_AGE_FOR_INHERITANCE) {
    const fatherDeathYears = Math.max(INHERIT_FATHER_MIN_YEARS, Math.round(LIFE_EVENT_FATHER_LIFE_EXPECTANCY - estimatedParentAge))
    if (fatherDeathYears < projectionYears) {
      const inheritAmount = income > LIFE_EVENT_INHERITANCE_HIGH_INCOME
        ? LIFE_EVENT_INHERITANCE_FATHER_HIGH
        : income > LIFE_EVENT_INHERITANCE_MID_INCOME
          ? LIFE_EVENT_INHERITANCE_FATHER_MID
          : LIFE_EVENT_INHERITANCE_FATHER_LOW
      recs.push({
        type: 'windfall',
        label: "Inheritance — Father's Estate",
        startYearOffset: fatherDeathYears,
        params: { amount: inheritAmount },
        probability: 0.55,
        rationale: `Fed Reserve SCF: ~50% receive inheritance. Median: $46k. US male life expectancy: 74.5 yrs.`,
      })
    }

    const motherDeathYears = Math.max(INHERIT_MOTHER_MIN_YEARS, Math.round(LIFE_EVENT_MOTHER_LIFE_EXPECTANCY - estimatedParentAge))
    if (motherDeathYears < projectionYears) {
      const inheritAmount2 = income > LIFE_EVENT_INHERITANCE_HIGH_INCOME
        ? LIFE_EVENT_INHERITANCE_MOTHER_HIGH
        : income > LIFE_EVENT_INHERITANCE_MID_INCOME
          ? LIFE_EVENT_INHERITANCE_MOTHER_MID
          : LIFE_EVENT_INHERITANCE_MOTHER_LOW
      recs.push({
        type: 'windfall',
        label: "Inheritance — Mother's Estate",
        startYearOffset: motherDeathYears,
        params: { amount: inheritAmount2 },
        probability: 0.50,
        rationale: `CDC 2022: US female life expectancy 80.2 yrs.`,
      })
    }
  }

  // ── Annual Performance Bonus ─────────────────────────────────────────────
  if (income > LIFE_EVENT_BONUS_INCOME_THRESHOLD && profile.annualW2Income > 0 && projectionYears > LIFE_EVENT_MIN_PROJECTION_BONUS) {
    recs.push({
      type: 'flat-bonus',
      label: 'Annual Performance Bonus',
      startYearOffset: 1,
      params: { amount: Math.round((income * LIFE_EVENT_BONUS_PCT) / BONUS_ROUNDING) * BONUS_ROUNDING, recurEveryYears: LIFE_EVENT_BONUS_CYCLE_YEARS },
      probability: 0.60,
      rationale: `WTW 2024: 78% of employers offer bonuses. Avg payout: 8–12% of salary.`,
    })
  }

  // ── Home Purchase (for renters) ──────────────────────────────────────────
  if (profile.homeEquity === 0 && currentAge < 55 && projectionYears > 4) {
    const yearsToPurchase = Math.max(1, Math.round(Math.max(0, LIFE_EVENT_FIRST_HOME_AGE - currentAge) * 0.6))
    const rawPrice = Math.round((income * LIFE_EVENT_HOME_PRICE_MULTIPLE) / HOME_PRICE_ROUNDING) * HOME_PRICE_ROUNDING
    recs.push({
      type: 'home-purchase',
      label: 'First Home Purchase',
      startYearOffset: clamp(yearsToPurchase, 1, projectionYears - 3),
      params: {
        homePrice: Math.max(LIFE_EVENT_MIN_HOME_PRICE, rawPrice),
        downPaymentPct: RENTER_HOME_DOWN_PCT,
        mortgageRate: RENTER_HOME_MORTGAGE_RATE,
        mortgageTermYears: RENTER_HOME_TERM_YEARS,
      },
      probability: 0.65,
      rationale: `NAR 2024: 65% own homes. Median first-time buyer age: 35. ~4-5× annual income.`,
    })
  }

  // ── Major Expense (vehicle, major repair, etc.) ──────────────────────────
  if (projectionYears > 6) {
    const vehicleYear = clamp(Math.round(projectionYears * LIFE_EVENT_VEHICLE_YEAR_PCT), 3, projectionYears - HALF)
    recs.push({
      type: 'major-expense',
      label: 'Major Expense (Vehicle / Renovation)',
      startYearOffset: vehicleYear,
      params: { amount: LIFE_EVENT_VEHICLE_COST, spreadYears: 1 },
      probability: 0.85,
      rationale: `AAA 2024: Americans replace vehicles every 7-8 years. Avg new vehicle: $48k.`,
    })
    const vehicle2Year = clamp(vehicleYear + LIFE_EVENT_VEHICLE_CYCLE_YEARS, vehicleYear + VEHICLE_MIN_GAP, projectionYears - HALF)
    if (vehicle2Year < projectionYears - 1) {
      recs.push({
        type: 'major-expense',
        label: 'Major Expense (Second Vehicle / Renovation)',
        startYearOffset: vehicle2Year,
        params: { amount: LIFE_EVENT_VEHICLE2_COST, spreadYears: 1 },
        probability: 0.70,
        rationale: `Second vehicle replacement cycle or major home renovation.`,
      })
    }
  }

  // ── Grandchild Care ──────────────────────────────────────────────────────
  const grandchildAge = LIFE_EVENT_GRANDCHILD_AGE - currentAge
  if (grandchildAge > 0 && grandchildAge < projectionYears - LIFE_EVENT_GRANDCHILD_END_GUARD && profile.childrenCount > 0) {
    recs.push({
      type: 'major-expense',
      label: 'Grandchild Support / Contributions',
      startYearOffset: clamp(grandchildAge, 1, projectionYears - HALF),
      params: { amount: LIFE_EVENT_GRANDCHILD_COST, spreadYears: LIFE_EVENT_GRANDCHILD_SPREAD },
      probability: 0.40,
      rationale: `AARP 2023: ~20% of grandparents contribute financially. Avg: $5k–$8k/year.`,
    })
  }

  return recs
}

/**
 * Algorithmic fallback: apply probabilities via seeded-ish random to get concrete events.
 * Uses profile birthYear as a seed offset for reproducibility within a session.
 */
export function generateAlgorithmicEvents(recs: EventRecommendation[], seed = 0): SimulationEvent[] {
  // Simple seeded pseudo-random: use profile birthYear so same inputs → same events
  let s = seed || Date.now()
  const rand = () => {
    s = (s * LCG_MULTIPLIER + LCG_INCREMENT) & LCG_MODULUS
    return (s >>> 0) / LCG_MODULUS
  }
  return recs
    .filter(rec => rand() < rec.probability)
    .map(rec => ({
      id: newEventId(),
      type: rec.type,
      label: rec.label,
      startYearOffset: rec.startYearOffset,
      params: rec.params,
    }))
}

/** Format the probability brief as a string for the LLM prompt. */
export function formatRecommendationsForLLM(
  recs: EventRecommendation[],
  currentAge: number,
  projectionYears: number,
  childrenCount: number,
): string {
  const lines = [
    `REALISTIC LIFE EVENT BRIEF — Age ${currentAge}, ${projectionYears}-year projection:`,
    ``,
    `Include ALL likely events (probability > 0.5) and selectively include lower-probability events.`,
    `Children: ${childrenCount} — ${childrenCount > 0 ? 'auto-include childcare + college tuition events.' : 'none entered.'}`,
    ``,
    `RECOMMENDED EVENTS (include in addEvents array):`,
  ]
  recs.forEach(rec => {
    lines.push(`• [${rec.type}] "${rec.label}" — Year ${rec.startYearOffset + 1} | prob: ${Math.round(rec.probability * 100)}%`)
    lines.push(`  Basis: ${rec.rationale}`)
  })
  lines.push(``)
  lines.push(`Generate a realistic addEvents array. Use recommended years as a guide but vary slightly.`)
  lines.push(`DO NOT include: retirement costs before age 75, childcare past child age 5, or`)
  lines.push(`events impossible given current age (${currentAge}).`)
  return lines.join('\n')
}

function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val))
}
